/**
 * Maps a homeScreenId to its corresponding productId
 *
 * Product IDs:
 * - 1: Básico (default navigation)
 * - 2: CRM (sidebarOptionsCRM)
 * - 3: Consola de Monitoreo (sidebarOptionsMonitoring)
 * - 4: Admin (sidebarOptionsAdmin)
 * - 5: API Keys (sidebarOptionsApiKeys)
 */

import * as screens from '@core/layouts/components/menu/screens';

/**
 * Mapping of screen IDs to their corresponding product IDs
 * Based on navigation structure in src/navigation/vertical/
 */
const SCREEN_TO_PRODUCT_MAP: Record<number, number> = {
  // Product 1 - Básico (general, apps, jobs, video, operations, telemetry, administrations)
  [screens.MAP]: 1,
  [screens.REPORTS]: 1,
  [screens.TRACKING_UNITS]: 1,
  [screens.SAFE_DRIVER]: 1,
  [screens.BACKLOG_JOBS]: 1,
  [screens.IMPORT_JOBS]: 1,
  [screens.JOBS_BOARD]: 1,
  [screens.JOBS_MONITOR]: 1,
  [screens.JOBS_REPORT]: 1,
  [screens.JOBS_WORK_ACTIVITY]: 1,
  [screens.VIDEO_DASHBOARD]: 1,
  [screens.VIDEO_EVENTS_HISTORY]: 1,
  [screens.VIDEO_HISTORY]: 1,
  [screens.LOGISTIC_ROUTE_CATALOG]: 1,
  [screens.LOGISTIC_ROUTE_MONITOR]: 1,
  [screens.LOGISTIC_ROUTE_REPORT]: 1,
  [screens.LOGISTIC_ROUTE_STATE_REPORT]: 1,
  [screens.ASSIGN_LOGISTIC_ROUTE]: 1,
  [screens.IMPORT_ROUTE_ASSIGNMENTS]: 1,
  [screens.WORK_PLAN_CATALOG]: 1,
  [screens.WORK_PLAN_MONITOR]: 1,
  [screens.WORK_PLAN_REPORT]: 1,
  [screens.SCHEDULED_WORKPLANS]: 1,
  [screens.ASSIGN_WORK_PLAN]: 1,
  [screens.WORKPLAN_IMPORT]: 1,
  [screens.PREVENTIVE_NOTICES]: 1,
  [screens.WORKSHOP_ADD_ORDER]: 1,
  [screens.SPAREPARTS_CATALOG]: 1,
  [screens.WORKMANSHIPS_CATALOG]: 1,
  [screens.SPAREPARTS_CATEGORIES_CATALOG]: 1,
  [screens.SPAREPARTS_PROVIDERS_CATALOG]: 1,
  [screens.MAINTENANCE_RANGES]: 1,
  [screens.GENERAL_MAINTENANCE_REPORT]: 1,
  [screens.BY_CATEGORY_REPORT]: 1,
  [screens.BY_UNIT_REPORT]: 1,
  [screens.BY_WORKSHOP_REPORT]: 1,
  [screens.IMPORT_MAINTENANCE_TICKETS]: 1,
  [screens.NEW_TICKET]: 1,
  [screens.RECORD_MULTIPLE_TICKETS]: 1,
  [screens.IMPORT_FUEL_TICKETS]: 1,
  [screens.FUEL_TICKETS_CONSOLIDATED_REPORT]: 1,
  [screens.FUEL_TICKETS_REPORT_BY_UNIT]: 1,
  [screens.FUEL_TICKETS_REPORT]: 1,
  [screens.OPTIMIZATIONS]: 1,
  [screens.TELEMETRY_UNITS]: 1,
  [screens.TEMPERATURE_TELEMETRY]: 1,
  [screens.FUEL_TELEMETRY_MONITOR]: 1,
  [screens.FUEL_TELEMETRY_REPORT]: 1,
  [screens.FUEL_TELEMETRY_REPORT_BY_VEHICLES]: 1,
  [screens.FUEL_TELEMETRY_REPORT_BY_VEHICLES_WEEKLY]: 1,
  [screens.TELEMETRY_FUEL_LOADS_REPORT]: 1,
  [screens.TELEMETRY_OBD2]: 1,
  [screens.OPERATOR]: 1,
  [screens.AGENT]: 1,
  [screens.ORGANIZATION]: 1,
  [screens.DIVISION]: 1,
  [screens.SUBDIVISION]: 1,
  [screens.GROUPS]: 1,
  [screens.SHIFT]: 1,
  [screens.TRIGGER]: 1,
  [screens.DRIVERS]: 1,
  [screens.PEOPLE]: 1,
  [screens.USERS]: 1,
  [screens.FORMS_CATALOG]: 1,
  [screens.ANSWERS_FORMS]: 1,
  [screens.DEVICES]: 1,
  [screens.SIMS]: 1,
  [screens.SENSORS]: 1,
  [screens.ASSETS]: 1,
  [screens.VEHICLES]: 1,
  [screens.EMAIL_NOTIFICATIONS]: 1,
  [screens.WEB_SERVICE_NOTIFICATIONS]: 1,
  [screens.SYSTEM_NOTIFICATIONS]: 1,
  [screens.TRACKER_NOTIFICATIONS]: 1,
  [screens.GEOFENCES_CATALOG]: 1,
  [screens.ASSIGNED_GEOFENCES]: 1,
  [screens.LOCATIONS_CATALOG]: 1,
  [screens.IMPORT_LOCATIONS]: 1,
  [screens.FLEET]: 1,

  // Product 2 - CRM (generalCRM)
  [screens.SERVICE_CLASSES]: 2,
  [screens.CONTRACT_TEMPLATES]: 2,
  [screens.CONTRACTS]: 2,
  [screens.QUOTATIONS]: 2,
  [screens.CUSTOMERS]: 2,
  [screens.CONTACTS]: 2,

  // Product 3 - Consola de Monitoreo (generalMonitoringCenter)
  [screens.CONSOLE_NOTIFICATIONS]: 3,
  [screens.PROTOCOL_CATALOG]: 3,
  [screens.MONITORING_CENTER_CONSOLE_REPORT]: 3,
  [screens.WHATSAPP]: 3,

  // Product 4 - Admin (generalAdmin)
  [screens.BUSINESS_PARTNERS]: 4,
  [screens.PUBLIC_REFERENCES_MAP]: 4,
  [screens.TEAM_ACTIONS]: 4,

  // Product 5 - API Keys (generalApiKeys)
  [screens.API_CONSUMPTION]: 5,
  [screens.WEB_SERVICE_RATES]: 5
};

/**
 * Gets the productId for a given homeScreenId
 * @param homeScreenId - The screen ID that the user has pinned as their home screen
 * @returns The corresponding productId (1-5), defaults to 1 if not found
 */
export function getProductIdFromHomeScreenId(homeScreenId: number | null | undefined): number {
  if (!homeScreenId) {
    return 1; // Default to Básico
  }

  return SCREEN_TO_PRODUCT_MAP[homeScreenId] || 1; // Default to Básico if screen not found
}
